import React, { useEffect, useState, ChangeEventHandler } from 'react';
import { getTextAreaStyles } from './styleUtils';
import { theme } from './theme';
import './TextArea.css';
import { Typography } from '../Typography/Typography';
import { ERROR_COLORS, GRAY_COLORS } from '../globalTheme';

export interface TextAreaProps {
	rows?: number;
	label?: string;
	placeholder?: string;
	hint?: string;
	loading?: boolean;
	error?: boolean;
	disabled?: boolean;
	required?: boolean;
	value?: string;
	onChange: ChangeEventHandler<HTMLTextAreaElement>;
	name?: string;
	id?: string;
	maxChars?: number;
	charLenghtError?: string;
	focused?: boolean;
	dataTestId?: string;
	autofocus?: boolean;
	strictMaxChars?: boolean;
	className?: string;

	hasCharLengthError?: (error: boolean) => void;
}

const TextArea: React.FC<TextAreaProps> = ({
	rows = 5,
	label,
	placeholder,
	hint,
	loading,
	error,
	disabled,
	required,
	value,
	onChange,
	name,
	id,
	charLenghtError,
	maxChars = 20000,
	focused = false,
	dataTestId,
	autofocus,
	hasCharLengthError,
	strictMaxChars = false,
	className
}) => {
	const [isFocused, setIsFocused] = useState(focused);
	const [charCount, setCharCount] = useState(0);
	const dynamicStyles = getTextAreaStyles(theme);

	useEffect(() => {
		if (charCount > maxChars) {
			if (hasCharLengthError) hasCharLengthError(true);
		} else {
			if (hasCharLengthError) hasCharLengthError(false);
		}
	}, [charCount]);

	const componentStyles = () => {
		const baseStyles = dynamicStyles.textArea;
		const focusedStyles = isFocused && !error ? dynamicStyles.textAreaFocused : {};
		const disabledStyles = disabled || loading ? dynamicStyles.textAreaDisabled : {};
		const errorStyles = error ? dynamicStyles.textAreaError : {};
		const errorFocusedStyles = isFocused && error ? dynamicStyles.textAreaErrorFocused : {};

		return {
			...baseStyles,
			...focusedStyles,
			...disabledStyles,
			...errorStyles,
			...errorFocusedStyles
		};
	};

	return (
		<div className="text-area-container">
			<label htmlFor={id} className="input-label" id="input-label">
				<Typography color={GRAY_COLORS.gray700}>{label}</Typography>
			</label>
			<div className={className ? `text-area ${className}` : 'text-area'}>
				<textarea
					style={componentStyles()}
					rows={rows}
					autoFocus={autofocus}
					data-testid={dataTestId}
					aria-label={label || 'input'}
					aria-describedby="input-label"
					aria-invalid={error ? 'true' : 'false'}
					aria-required={required ? 'true' : 'false'}
					onFocus={() => setIsFocused(true)}
					onBlur={() => setIsFocused(false)}
					placeholder={placeholder}
					value={value}
					onChange={(e) => {
						if (typeof onChange === 'function') {
							onChange(e);
						}
						setCharCount(e.target.value.length);
					}}
					name={name}
					id={id}
					maxLength={strictMaxChars ? maxChars : undefined}
					disabled={disabled || loading}
					required={required}
					className="input-field"
				/>
			</div>
			<Typography size="sm" color={error ? 'error' : 'subtitle'}>
				{hint}
			</Typography>
			{charCount > maxChars && (
				<div>
					<Typography color={ERROR_COLORS.error500} size="sm">
						{charLenghtError}
					</Typography>
				</div>
			)}
		</div>
	);
};

export default TextArea;
